import { CssBaseline, ThemeProvider } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { lightTheme } from "@/theme";
import {
  useAuthChangePassword,
  useAuthLogin,
  useAuthRequestPasswordReset,
  useAuthSendSmsToken,
  useAuthSetNewPassword,
  useAuthVerifyAppToken,
  useAuthVerifySmsToken,
} from "@/api/generated/auth/auth";
import { BffError } from "@/api/generated/schemas";
import { ChangePasswordComplete } from "./components/login/ChangePasswordComplete";
import { ChangePasswordForm } from "./components/login/ChangePasswordForm";
import { ForgotPasswordForm } from "./components/login/ForgotPasswordForm";
import { LoginForm } from "./components/login/LoginForm";
import { pickRandomLoginNote } from "./components/login/pickRandomLoginNote";
import { useAuthSuccessRedirect } from "./components/login/useAuthSuccessRedirect";
import { LoginLayout } from "./components/login/LoginLayout";
import { SetNewPasswordForm } from "./components/login/SetNewPasswordForm";
import { PasswordResetComplete } from "./components/login/PasswordResetComplete";
import { PasswordResetSent } from "./components/login/PasswordResetSent";
import { SmsNotPossible } from "./components/login/SmsNotPossible";
import { TwoFactorForm } from "./components/login/TwoFactorForm";
import { startMicrosoftLogin } from "./startMicrosoftLogin";

export type AuthBootstrap = {
  mode: string;
  tenantName: string;
  redirectUrl: string;
  passwordResetSuccessUrl: string;
  initialEmail: string;
  uidb64: string;
  token: string;
  validLink: boolean;
  ssoEnabled: boolean;
  microsoftLoginDisplayName: string;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 0, refetchOnWindowFocus: false },
    mutations: { retry: 0 },
  },
});

/**
 * Appends the post-login `next` target to an auth navigation URL so the deep
 * link survives intermediate steps (e.g. switching between app and SMS 2FA).
 * Without this, switching 2FA method drops `next` and the user lands on `/`.
 */
export const buildAuthUrlWithNext = (path: string, next: string): string => {
  if (!next) {
    return path;
  }
  return `${path}?next=${encodeURIComponent(next)}`;
};

type LoginScreenProps = {
  bootstrap: AuthBootstrap;
};

const readLoginErrorFromQuery = (): string | undefined => {
  const error = new URLSearchParams(window.location.search).get("error");
  return error?.trim() || undefined;
};

const LoginScreen = ({ bootstrap }: LoginScreenProps) => {
  const { t } = useTranslation("auth", { keyPrefix: "login" });
  const [error, setError] = useState<string | undefined>(() =>
    readLoginErrorFromQuery(),
  );
  const [microsoftLoginLoading, setMicrosoftLoginLoading] = useState(false);
  const { success: loginSuccess, handleAuthSuccess } = useAuthSuccessRedirect();
  const [loginNote] = useState(() =>
    pickRandomLoginNote(t(($) => $.loginNotes, { returnObjects: true })),
  );

  const loginMutation = useAuthLogin<BffError>({
    mutation: {
      onSuccess: (result) => {
        handleAuthSuccess(result.redirect_url);
      },
      onError: (apiError) => {
        setError(apiError.message || t(($) => $.invalidCredentials));
      },
    },
  });

  const handleSubmit = (email: string, password: string) => {
    setError(undefined);
    loginMutation.mutate({
      data: {
        email,
        password,
        next: bootstrap.redirectUrl || undefined,
      },
    });
  };

  const handleMicrosoftLogin = async () => {
    setError(undefined);
    setMicrosoftLoginLoading(true);
    try {
      await startMicrosoftLogin(bootstrap.redirectUrl);
    } catch (apiError) {
      const message =
        apiError && typeof apiError === "object" && "message" in apiError
          ? String((apiError as BffError).message || "")
          : "";
      setError(message || t(($) => $.microsoftLoginError));
      setMicrosoftLoginLoading(false);
    }
  };

  const microsoftLabel = bootstrap.microsoftLoginDisplayName
    ? t(($) => $.continueWithOrganizationAccount, {
        organizationName: bootstrap.microsoftLoginDisplayName,
      })
    : t(($) => $.continueWithMicrosoft);

  return (
    <LoginForm
      onSubmit={handleSubmit}
      identityProviders={
        bootstrap.ssoEnabled
          ? [
              {
                id: "microsoft",
                label: microsoftLabel,
                onSelect: handleMicrosoftLogin,
                loading: microsoftLoginLoading,
              },
            ]
          : []
      }
      onClearError={() => setError(undefined)}
      error={error}
      loading={loginMutation.isPending}
      success={loginSuccess}
      tenantName={bootstrap.tenantName || undefined}
      headerIcon={loginNote.icon}
      translations={{
        welcomeBack: loginNote.title,
        signInTitle: loginNote.title,
        signInSubtitle: loginNote.subtitle,
        ssoSignInSubtitle: t(($) => $.ssoSignInSubtitle),
        signIn: t(($) => $.signIn),
        partnerOrExternalPrompt: t(($) => $.partnerOrExternalPrompt),
        signInWithEmailPassword: t(($) => $.signInWithEmailPassword),
        backToOrganizationLogin: t(($) => $.backToOrganizationLogin),
        email: t(($) => $.email),
        password: t(($) => $.password),
        emailPlaceholder: t(($) => $.emailPlaceholder),
        forgotPassword: t(($) => $.forgotPassword),
        fieldRequired: t(($) => $.fieldRequired),
        invalidEmail: t(($) => $.invalidEmail),
      }}
    />
  );
};

type VerifyAppTokenScreenProps = {
  bootstrap: AuthBootstrap;
};

const VerifyAppTokenScreen = ({ bootstrap }: VerifyAppTokenScreenProps) => {
  const { t } = useTranslation("auth", { keyPrefix: "login" });
  const [error, setError] = useState<string | undefined>(undefined);
  const { success: verifySuccess, handleAuthSuccess } =
    useAuthSuccessRedirect();

  const verifyMutation = useAuthVerifyAppToken<BffError>({
    mutation: {
      onSuccess: (result) => {
        handleAuthSuccess(result.redirect_url);
      },
      onError: (apiError) => {
        setError(apiError.message || t(($) => $.invalidToken));
      },
    },
  });

  const handleSubmit = (token: string) => {
    setError(undefined);
    verifyMutation.mutate({
      data: { token, next: bootstrap.redirectUrl || undefined },
    });
  };

  return (
    <TwoFactorForm
      mode="app"
      onSubmit={handleSubmit}
      error={error}
      loading={verifyMutation.isPending}
      success={verifySuccess}
      switchUrl={buildAuthUrlWithNext(
        "/accounts/verify_sms_token/",
        bootstrap.redirectUrl,
      )}
      translations={{
        verifyIdentity: t(($) => $.verifyIdentity),
        appTokenInstructions: t(($) => $.appTokenInstructions),
        smsTokenInstructions: t(($) => $.smsTokenInstructions),
        token: t(($) => $.token),
        tokenPlaceholder: t(($) => $.tokenPlaceholder),
        continue: t(($) => $.continue),
        switchToSms: t(($) => $.switchToSms),
        smsToken: t(($) => $.smsToken),
        switchToApp: t(($) => $.switchToApp),
        appToken: t(($) => $.appToken),
        resendToken: t(($) => $.resendToken),
        tokenResent: t(($) => $.tokenResent),
        fieldRequired: t(($) => $.fieldRequired),
      }}
    />
  );
};

type VerifySmsTokenScreenProps = {
  bootstrap: AuthBootstrap;
};

const VerifySmsTokenScreen = ({ bootstrap }: VerifySmsTokenScreenProps) => {
  const { t } = useTranslation("auth", { keyPrefix: "login" });
  const [error, setError] = useState<string | undefined>(undefined);
  const [resendSuccess, setResendSuccess] = useState(false);
  const { success: verifySuccess, handleAuthSuccess } =
    useAuthSuccessRedirect();

  const sendMutation = useAuthSendSmsToken<BffError>({
    mutation: {
      onError: (apiError) => {
        setError(apiError.message || t(($) => $.invalidToken));
      },
    },
  });

  const verifyMutation = useAuthVerifySmsToken<BffError>({
    mutation: {
      onSuccess: (result) => {
        handleAuthSuccess(result.redirect_url);
      },
      onError: (apiError) => {
        setError(apiError.message || t(($) => $.invalidToken));
      },
    },
  });

  // Auto-dispatch the first SMS on mount, mirroring the legacy behaviour
  // where opening the page implicitly sent a code. The ref guard prevents
  // React.StrictMode's dev-time double effect from firing two SMSes.
  const hasAutoSentRef = useRef(false);
  useEffect(() => {
    if (hasAutoSentRef.current) {
      return;
    }
    hasAutoSentRef.current = true;
    sendMutation.mutate();
  }, [sendMutation]);

  const handleResend = () => {
    setError(undefined);
    setResendSuccess(false);
    sendMutation.mutate(undefined, {
      onSuccess: () => setResendSuccess(true),
    });
  };

  const handleSubmit = (token: string) => {
    setError(undefined);
    setResendSuccess(false);
    verifyMutation.mutate({
      data: { token, next: bootstrap.redirectUrl || undefined },
    });
  };

  return (
    <TwoFactorForm
      mode="sms"
      onSubmit={handleSubmit}
      onResend={handleResend}
      error={error}
      loading={verifyMutation.isPending}
      success={verifySuccess}
      resendSuccess={resendSuccess}
      switchUrl={buildAuthUrlWithNext(
        "/accounts/verify_app_token/",
        bootstrap.redirectUrl,
      )}
      translations={{
        verifyIdentity: t(($) => $.verifyIdentity),
        appTokenInstructions: t(($) => $.appTokenInstructions),
        smsTokenInstructions: t(($) => $.smsTokenInstructions),
        token: t(($) => $.token),
        tokenPlaceholder: t(($) => $.tokenPlaceholder),
        continue: t(($) => $.continue),
        switchToSms: t(($) => $.switchToSms),
        smsToken: t(($) => $.smsToken),
        switchToApp: t(($) => $.switchToApp),
        appToken: t(($) => $.appToken),
        resendToken: t(($) => $.resendToken),
        tokenResent: t(($) => $.tokenResent),
        fieldRequired: t(($) => $.fieldRequired),
      }}
    />
  );
};

const SmsNotPossibleScreen = () => {
  const { t } = useTranslation("auth", { keyPrefix: "login" });
  return (
    <SmsNotPossible
      translations={{
        verifyIdentity: t(($) => $.verifyIdentity),
        smsNotPossible: t(($) => $.smsNotPossible),
        backToLogin: t(($) => $.backToLogin),
      }}
    />
  );
};

type RequestPasswordResetScreenProps = {
  bootstrap: AuthBootstrap;
};

const RequestPasswordResetScreen = ({
  bootstrap,
}: RequestPasswordResetScreenProps) => {
  const { t } = useTranslation("auth", { keyPrefix: "login" });
  const [error, setError] = useState<string | undefined>(undefined);

  const mutation = useAuthRequestPasswordReset<BffError>({
    mutation: {
      onSuccess: (result) => {
        window.location.href = result.redirect_url;
      },
      onError: (apiError) => {
        setError(apiError.message || t(($) => $.passwordResetError));
      },
    },
  });

  const handleSubmit = (email: string) => {
    setError(undefined);
    mutation.mutate({
      data: {
        email,
        success_url:
          bootstrap.passwordResetSuccessUrl || "/accounts/password_reset/done/",
      },
    });
  };

  return (
    <ForgotPasswordForm
      onSubmit={handleSubmit}
      error={error}
      loading={mutation.isPending}
      initialEmail={bootstrap.initialEmail}
      translations={{
        resetPassword: t(($) => $.resetPassword),
        resetPasswordInstructions: t(($) => $.resetPasswordInstructions),
        email: t(($) => $.email),
        emailPlaceholder: t(($) => $.emailPlaceholder),
        sendResetLink: t(($) => $.sendResetLink),
        backToLogin: t(($) => $.backToLogin),
        fieldRequired: t(($) => $.fieldRequired),
        invalidEmail: t(($) => $.invalidEmail),
      }}
    />
  );
};

const PasswordResetSentScreen = () => {
  const { t } = useTranslation("auth", { keyPrefix: "login" });
  return (
    <PasswordResetSent
      translations={{
        resetPasswordSent: t(($) => $.resetPasswordSent),
        resetPasswordSentDescription: t(($) => $.resetPasswordSentDescription),
        backToLogin: t(($) => $.backToLogin),
      }}
    />
  );
};

type SetNewPasswordScreenProps = {
  bootstrap: AuthBootstrap;
};

const SetNewPasswordScreen = ({ bootstrap }: SetNewPasswordScreenProps) => {
  const { t } = useTranslation("auth", { keyPrefix: "login" });
  const [error, setError] = useState<string | undefined>(undefined);

  const mutation = useAuthSetNewPassword<BffError>({
    mutation: {
      onSuccess: (result) => {
        window.location.href = result.redirect_url;
      },
      onError: (apiError) => {
        setError(apiError.message || t(($) => $.passwordResetError));
      },
    },
  });

  const handleSubmit = (newPassword: string) => {
    setError(undefined);
    mutation.mutate({
      data: {
        uidb64: bootstrap.uidb64,
        token: bootstrap.token,
        new_password1: newPassword,
        new_password2: newPassword,
      },
    });
  };

  return (
    <SetNewPasswordForm
      onSubmit={handleSubmit}
      error={error}
      loading={mutation.isPending}
      validLink={bootstrap.validLink}
      translations={{
        resetPassword: t(($) => $.resetPassword),
        newPassword: t(($) => $.newPassword),
        confirmPassword: t(($) => $.confirmPassword),
        setNewPassword: t(($) => $.setNewPassword),
        fieldRequired: t(($) => $.fieldRequired),
        passwordsDoNotMatch: t(($) => $.passwordsDoNotMatch),
        passwordTooShort: t(($) => $.passwordTooShort),
        passwordResetUnsuccessful: t(($) => $.passwordResetUnsuccessful),
        passwordResetLinkInvalid: t(($) => $.passwordResetLinkInvalid),
        backToLogin: t(($) => $.backToLogin),
      }}
    />
  );
};

const PasswordResetCompleteScreen = () => {
  const { t } = useTranslation("auth", { keyPrefix: "login" });
  return (
    <PasswordResetComplete
      translations={{
        passwordResetComplete: t(($) => $.passwordResetComplete),
        passwordResetCompleteDescription: t(
          ($) => $.passwordResetCompleteDescription,
        ),
        goToLogin: t(($) => $.goToLogin),
      }}
    />
  );
};

const ChangePasswordScreen = () => {
  const { t } = useTranslation("auth", { keyPrefix: "login" });
  const [error, setError] = useState<string | undefined>(undefined);

  const mutation = useAuthChangePassword<BffError>({
    mutation: {
      onSuccess: (result) => {
        window.location.href = result.redirect_url;
      },
      onError: (apiError) => {
        setError(apiError.message || t(($) => $.passwordChangeError));
      },
    },
  });

  const handleSubmit = (oldPassword: string, newPassword: string) => {
    setError(undefined);
    mutation.mutate({
      data: {
        old_password: oldPassword,
        new_password1: newPassword,
        new_password2: newPassword,
      },
    });
  };

  return (
    <ChangePasswordForm
      onSubmit={handleSubmit}
      error={error}
      loading={mutation.isPending}
      translations={{
        changePassword: t(($) => $.changePassword),
        changePasswordInstructions: t(($) => $.changePasswordInstructions),
        oldPassword: t(($) => $.oldPassword),
        newPassword: t(($) => $.newPassword),
        confirmPassword: t(($) => $.confirmPassword),
        changeMyPassword: t(($) => $.changeMyPassword),
        fieldRequired: t(($) => $.fieldRequired),
        passwordsDoNotMatch: t(($) => $.passwordsDoNotMatch),
        passwordTooShort: t(($) => $.passwordTooShort),
      }}
    />
  );
};

const ChangePasswordCompleteScreen = () => {
  const { t } = useTranslation("auth", { keyPrefix: "login" });
  return (
    <ChangePasswordComplete
      translations={{
        passwordChangeComplete: t(($) => $.passwordChangeComplete),
        passwordChangeCompleteDescription: t(
          ($) => $.passwordChangeCompleteDescription,
        ),
        continueToCube: t(($) => $.continueToCube),
      }}
    />
  );
};

const UnknownMode = ({ mode }: { mode: string }) => (
  <div style={{ padding: "2rem", textAlign: "center" }}>
    Unknown auth mode: <code>{mode}</code>
  </div>
);

const renderForMode = (bootstrap: AuthBootstrap) => {
  switch (bootstrap.mode) {
    case "login":
      return <LoginScreen bootstrap={bootstrap} />;
    case "verify_app_token":
      return <VerifyAppTokenScreen bootstrap={bootstrap} />;
    case "verify_sms_token":
      return <VerifySmsTokenScreen bootstrap={bootstrap} />;
    case "sms_not_possible":
      return <SmsNotPossibleScreen />;
    case "request_password_reset":
      return <RequestPasswordResetScreen bootstrap={bootstrap} />;
    case "password_reset_sent":
      return <PasswordResetSentScreen />;
    case "set_new_password":
      return <SetNewPasswordScreen bootstrap={bootstrap} />;
    case "password_reset_complete":
      return <PasswordResetCompleteScreen />;
    case "change_password":
      return <ChangePasswordScreen />;
    case "change_password_complete":
      return <ChangePasswordCompleteScreen />;
    default:
      return <UnknownMode mode={bootstrap.mode} />;
  }
};

type AuthAppProps = {
  bootstrap: AuthBootstrap;
};

const AuthLoginLayout = ({
  bootstrap,
  children,
}: {
  bootstrap: AuthBootstrap;
  children: ReactNode;
}) => {
  const { t } = useTranslation("auth", { keyPrefix: "login" });

  return (
    <LoginLayout
      tenantName={bootstrap.tenantName || undefined}
      poweredByText={t(($) => $.poweredBy)}
      tenantPoweredByText={t(($) => $.tenantPoweredBy)}
    >
      {children}
    </LoginLayout>
  );
};

export const AuthApp = ({ bootstrap }: AuthAppProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <AuthLoginLayout bootstrap={bootstrap}>
          {renderForMode(bootstrap)}
        </AuthLoginLayout>
      </ThemeProvider>
    </QueryClientProvider>
  );
};
